import { Suspense, lazy, useEffect, useMemo, type ErrorInfo } from 'react';
import { AppRoutes } from './routes/AppRoutes';
import { reactPlugin } from './AppInsights';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';

import {
  BrowserRouter as Router
} from 'react-router-dom';
import Footer from './components/Footer/Footer';
import { selectCommon, setIsTablet } from './store/commonSlice';
import { useAppDispatch, useAppSelector } from './store/hooks';
import debounce from 'lodash.debounce';
import { ErrorBoundary } from 'react-error-boundary';
import React from 'react';


const ErrorPage = lazy(() => import('./containers/ErrorPage/ErrorPage'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop/ScrollToTop'));
const ErrorBanner = lazy(() => import('./components/ErrorBanner/ErrorBanner'));
const PageLoading = lazy(() => import('./components/PageLoading/PageLoading'));

function App(): React.JSX.Element {
  const dispatch = useAppDispatch();
  const common = useAppSelector(selectCommon);

  /* Error handler for ErrorBoundary */
  const errorHandler = (
    error: Error,
   info: ErrorInfo
  ): void => {
    console.log(error, info.componentStack);
    window.location.pathname = '/loading-error';
  };

  /* Add window resize listener to detect tablet */
  const debouncedFunction = (): void => {
    if (window.innerWidth <= 768) {
      dispatch(setIsTablet(true));
    } else {
      dispatch(setIsTablet(false));
    }
  };

  // useMemo: keep one instance of the debounced function between component re-renderings
  const debouncedHandler = useMemo(
    () => debounce(debouncedFunction, 250),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* Prevent window to navigate back (using browser back button)*/
  const windowNavigationHandler = (e: PopStateEvent): void => {
    e.preventDefault();
    window.history.pushState(null, document.title, window.location.href);
  };

  useEffect(() => {
    window.addEventListener('resize', debouncedHandler);
    window.addEventListener('popstate', windowNavigationHandler, false);

    return (): void => {
      // Stop  the invocation of the debounced function after unmounting
      debouncedHandler.cancel();

      window.removeEventListener('resize', debouncedHandler);
      window.removeEventListener('popstate', windowNavigationHandler);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
      <Router>
    <AppInsightsContext.Provider value={reactPlugin}>
        <ErrorBoundary
          FallbackComponent={ErrorPage}
          onError={errorHandler}
        >
          {common.errorBannerType && (
            <ErrorBanner
              errorType={common.errorBannerType}
              disableForm={false}
            />
          )}

          <ScrollToTop />
          <PageLoading />

          <Suspense fallback={<></>}>
                  <AppRoutes />
          </Suspense>
        </ErrorBoundary>

        <Footer />
      </AppInsightsContext.Provider>
    </Router>
  );
}

export default App;
